import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  Popover,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import padStart from "lodash/padStart";

export interface PreExistingConditionMonthsEditorRef {
  getValue: () => string;
}

type FormatType = "XX/XX" | "XX/XX/XX";

interface MonthsValues {
  monthsDiagnosisFreeBeforeEffDate: string;
  monthsTreatmentFree: string;
  monthsDiagnosisFreeAfterEffDate: string;
}

const padMonth = (value: string): string =>
  value ? padStart(value, 2, "0") : "00";

/**
 * Build the positional `before/treatment[/after]` string the grid cell stores
 * and renders verbatim.
 *
 * In the three-field format a blank or zero treatment-free value reads the same
 * as one the carrier never stated, so it collapses to "before/after" rather
 * than carrying a "00" middle segment (CD-1905). The two-field format keeps a
 * zero, where the second position is the treatment-free value itself.
 */
const buildMonthsDetail = (
  format: FormatType,
  values: MonthsValues,
): string => {
  const beforeEffDate = padMonth(values.monthsDiagnosisFreeBeforeEffDate);
  const treatmentFree = padMonth(values.monthsTreatmentFree);

  if (format === "XX/XX") {
    return `${beforeEffDate}/${treatmentFree}`;
  }

  const afterEffDate = padMonth(values.monthsDiagnosisFreeAfterEffDate);
  return treatmentFree === "00"
    ? `${beforeEffDate}/${afterEffDate}`
    : `${beforeEffDate}/${treatmentFree}/${afterEffDate}`;
};

/**
 * Popover-based editor for Pre-Existing Condition months detail
 * Allows user to select format (XX/XX or XX/XX/XX) and enter month values
 */
export const PreExistingConditionMonthsEditor = forwardRef<
  PreExistingConditionMonthsEditorRef,
  CustomCellEditorProps
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const initialValue: string =
    typeof props.value === "string" ? props.value : "";

  // Parse initial value
  const parseValue = (
    value: string,
  ): { format: FormatType; values: MonthsValues } => {
    if (!value) {
      return {
        format: "XX/XX",
        values: {
          monthsDiagnosisFreeBeforeEffDate: "",
          monthsTreatmentFree: "",
          monthsDiagnosisFreeAfterEffDate: "",
        },
      };
    }

    const parts = value.split("/");
    if (parts.length === 2) {
      return {
        format: "XX/XX",
        values: {
          monthsDiagnosisFreeBeforeEffDate: parts[0],
          monthsTreatmentFree: parts[1],
          monthsDiagnosisFreeAfterEffDate: "",
        },
      };
    } else if (parts.length === 3) {
      return {
        format: "XX/XX/XX",
        values: {
          monthsDiagnosisFreeBeforeEffDate: parts[0],
          monthsTreatmentFree: parts[1],
          monthsDiagnosisFreeAfterEffDate: parts[2],
        },
      };
    }

    return {
      format: "XX/XX",
      values: {
        monthsDiagnosisFreeBeforeEffDate: "",
        monthsTreatmentFree: "",
        monthsDiagnosisFreeAfterEffDate: "",
      },
    };
  };

  const parsed = parseValue(initialValue);
  const [format, setFormat] = useState<FormatType>(parsed.format);
  const [values, setValues] = useState<MonthsValues>(parsed.values);

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Expose getValue method to AG Grid
  useImperativeHandle(ref, () => ({
    getValue: () => buildMonthsDetail(format, values),
  }));

  // Auto-open popover when editor is created
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = (): void => {
    setOpen(false);
    if (stopEditing) {
      setTimeout(() => {
        stopEditing();
      }, 0);
    }
  };

  const handleDone = (): void => {
    const finalValue = buildMonthsDetail(format, values);

    // Set the data value directly on the node
    if (node && column) {
      const columnField = column.getColId();
      node.setDataValue(columnField, finalValue);
    }

    // Stop editing and refresh
    if (stopEditing) {
      stopEditing();
    }

    if (api && node) {
      setTimeout(() => {
        api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    }

    setOpen(false);
  };

  const handleFormatChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setFormat(event.target.value as FormatType);
  };

  const handleValueChange = (
    field: keyof MonthsValues,
    value: string,
  ): void => {
    // Only allow numeric input
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEnterKey = (event: React.KeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      handleDone();
    }
  };

  return (
    <>
      <div ref={anchorRef} style={{ width: "100%", height: "100%" }} />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableEnforceFocus
        disableAutoFocus={false}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxWidth: 380,
              p: 2,
            },
          },
        }}
      >
        <Box onClick={(e) => e.stopPropagation()} onKeyDown={handleEnterKey}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Pre-Existing Condition format
          </FormLabel>

          <RadioGroup value={format} onChange={handleFormatChange}>
            <FormControlLabel
              value="XX/XX"
              control={<Radio size="small" />}
              label="XX/XX"
            />
            <FormControlLabel
              value="XX/XX/XX"
              control={<Radio size="small" />}
              label="XX/XX/XX"
            />
          </RadioGroup>

          <FormHelperText sx={{ mt: 0, mb: 2 }}>
            Enter values below
          </FormHelperText>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              value={values.monthsDiagnosisFreeBeforeEffDate}
              onChange={(e) => {
                e.stopPropagation();
                handleValueChange(
                  "monthsDiagnosisFreeBeforeEffDate",
                  e.target.value,
                );
              }}
              onFocus={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleEnterKey}
              placeholder="00"
              size="small"
              type="text"
              inputProps={{
                maxLength: 2,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{ width: 60 }}
            />
            <Typography>/</Typography>
            <TextField
              value={values.monthsTreatmentFree}
              onChange={(e) => {
                e.stopPropagation();
                handleValueChange("monthsTreatmentFree", e.target.value);
              }}
              onFocus={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleEnterKey}
              placeholder="00"
              size="small"
              type="text"
              inputProps={{
                maxLength: 2,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{ width: 60 }}
            />
            {format === "XX/XX/XX" && (
              <>
                <Typography>/</Typography>
                <TextField
                  value={values.monthsDiagnosisFreeAfterEffDate}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleValueChange(
                      "monthsDiagnosisFreeAfterEffDate",
                      e.target.value,
                    );
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleEnterKey}
                  placeholder="00"
                  size="small"
                  type="text"
                  inputProps={{
                    maxLength: 2,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{ width: 60 }}
                />
              </>
            )}
          </Box>

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button onClick={handleDone} variant="primary">
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
});

PreExistingConditionMonthsEditor.displayName =
  "PreExistingConditionMonthsEditor";
